import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import llinsoft.site.components.layouts.MarkdownLayout as _llinsoft_site_components_layouts_MarkdownLayout
import llinsoft.site.components.layouts.PageLayout as _llinsoft_site_components_layouts_PageLayout
import llinsoft.site.pages.AboutPage as _llinsoft_site_pages_AboutPage
import llinsoft.site.pages.HomePage as _llinsoft_site_pages_HomePage
import llinsoft.site.pages.projects.ProjectSlugPage as _llinsoft_site_pages_projects_ProjectSlugPage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "portfolio-website"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "llinsoft.site.components.layouts.MarkdownLayout",
            parentLayoutId = "llinsoft.site.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                llinsoft.site.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _llinsoft_site_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "llinsoft.site.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _llinsoft_site_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "llinsoft.site.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                llinsoft.site.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _llinsoft_site_pages_HomePage()
        }
        ctx.router.register(
            "/about",
            layoutId = "llinsoft.site.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                llinsoft.site.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _llinsoft_site_pages_AboutPage()
        }
        ctx.router.register(
            "/projects/{slug}",
            layoutId = "llinsoft.site.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                llinsoft.site.pages.projects.initProjectSlugPage(ctx)
            },
        ) { pageCtx -> 
            _llinsoft_site_pages_projects_ProjectSlugPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", llinsoft.site.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", llinsoft.site.SubheadlineTextStyle)
        ctx.theme.registerStyle("mono-tag-text", llinsoft.site.MonoTagTextStyle)
        ctx.theme.registerStyle("project-section-container", llinsoft.site.components.widgets.ProjectSectionContainerStyle)
        ctx.theme.registerStyle("section-heading", llinsoft.site.components.widgets.SectionHeadingStyle)
        ctx.theme.registerStyle("project-card-inner", llinsoft.site.components.widgets.ProjectCardInnerStyle)
        ctx.theme.registerStyle("project-title", llinsoft.site.components.widgets.ProjectTitleStyle)
        ctx.theme.registerStyle("project-desc", llinsoft.site.components.widgets.ProjectDescStyle)
        ctx.theme.registerStyle("project-tag", llinsoft.site.components.widgets.ProjectTagStyle)
        ctx.theme.registerStyle("gallery-lightbox-overlay", llinsoft.site.components.widgets.GalleryLightboxOverlayStyle)
        ctx.theme.registerStyle("gallery-lightbox-panel", llinsoft.site.components.widgets.GalleryLightboxPanelStyle)
        ctx.theme.registerStyle("gallery-lightbox-stage-shell", llinsoft.site.components.widgets.GalleryLightboxStageShellStyle)
        ctx.theme.registerStyle("gallery-lightbox-image-stage", llinsoft.site.components.widgets.GalleryLightboxImageStageStyle)
        ctx.theme.registerStyle("project-hero-title", llinsoft.site.components.widgets.ProjectHeroTitleStyle)
        ctx.theme.registerStyle("footer", llinsoft.site.components.sections.FooterStyle)
        ctx.theme.registerStyle("projects-title", llinsoft.site.components.sections.ProjectsTitleStyle)
        ctx.theme.registerStyle("projects-subtitle", llinsoft.site.components.sections.ProjectsSubtitleStyle)
        ctx.theme.registerStyle("projects-grid", llinsoft.site.components.sections.ProjectsGridStyle)
        ctx.theme.registerStyle("hero-name", llinsoft.site.components.sections.HeroNameStyle)
        ctx.theme.registerStyle("hero-role", llinsoft.site.components.sections.HeroRoleStyle)
        ctx.theme.registerStyle("hero-headline", llinsoft.site.components.sections.HeroHeadlineStyle)
        ctx.theme.registerStyle("hero-support-line", llinsoft.site.components.sections.HeroSupportLineStyle)
        ctx.theme.registerStyle("control-wrapper", llinsoft.site.components.sections.ControlWrapperStyle)
        ctx.theme.registerStyle("proof-chip", llinsoft.site.components.sections.ProofChipStyle)
        ctx.theme.registerStyle("hero-avatar-shell", llinsoft.site.components.sections.HeroAvatarShellStyle)
        ctx.theme.registerStyle("bio-container", llinsoft.site.components.sections.BioContainerStyle)
        ctx.theme.registerStyle("bio-title", llinsoft.site.components.sections.BioTitleStyle)
        ctx.theme.registerStyle("bio-card", llinsoft.site.components.sections.BioCardStyle)
        ctx.theme.registerStyle("bio-name", llinsoft.site.components.sections.BioNameStyle)
        ctx.theme.registerStyle("bio-role", llinsoft.site.components.sections.BioRoleStyle)
        ctx.theme.registerStyle("bio-paragraph", llinsoft.site.components.sections.BioParagraphStyle)
        ctx.theme.registerStyle("nav-header", llinsoft.site.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("markdown", llinsoft.site.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("page-content", llinsoft.site.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("project-page", llinsoft.site.pages.projects.ProjectPageStyle)
        ctx.theme.registerStyle("project-top-nav", llinsoft.site.pages.projects.ProjectTopNavStyle)
        ctx.theme.registerStyle("project-section", llinsoft.site.pages.projects.ProjectSectionStyle)
        ctx.theme.registerStyle("project-detail-title", llinsoft.site.pages.projects.ProjectDetailTitleStyle)
        ctx.theme.registerStyle("project-section-heading", llinsoft.site.pages.projects.ProjectSectionHeadingStyle)
        ctx.theme.registerVariant("-circle", llinsoft.site.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", llinsoft.site.UncoloredButtonVariant
        )
        llinsoft.site.initColorMode(ctx)
        llinsoft.site.initStyles(ctx)
        llinsoft.site.initSiteStyles(ctx)
        llinsoft.site.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        llinsoft.site.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
